"use client"
import {
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react"
import { useHudContext } from "../../hud/shared/playbookHUDInfra"
import { type LegendPosition } from "../sideways/dithered/GroupedSquaresLegend"

// Matches the mt-3 / mb-3 legend gap in LEGEND_CONTAINER_STYLE
// (GroupedSquaresLegend).
const DEFAULT_LEGEND_GAP_PX = 12
// Never shrink the grid below this (~3 rows of 10px squares), even on
// very short viewports.
const DEFAULT_GRID_MIN_HEIGHT_PX = 32
// Cap rounds down to this step so 1px layout jitter above the chart
// doesn't trigger extra re-renders / canvas re-sizing.
const CAP_QUANTIZE_PX = 8

// Caps grid height so the legend always fits on screen below it. In
// the playbook the chart lives in a fixed-height HUD container, so
// the budget is that container's height minus the wrapper's offset
// from its top (this cancels the HUD's scroll-driven slide-up
// transform, which shifts both rects equally). Outside the HUD it
// falls back to the viewport. Either way the budget never exceeds
// the viewport height, so the grid can't outgrow the screen.
export const useGridMaxHeightWithLegendVisible = ({
  maxHeight,
  legendPosition,
  legendRef,
  legendGapPx = DEFAULT_LEGEND_GAP_PX,
  minHeightPx = DEFAULT_GRID_MIN_HEIGHT_PX,
  initialHeightPx,
}: {
  // Upper bound on the returned cap. Pass Infinity to let the grid grow
  // until it's limited by the screen alone.
  maxHeight: number
  legendPosition: LegendPosition
  legendRef: RefObject<HTMLDivElement | null>
  // Vertical gap (px) between the grid and a top/bottom legend, reserved
  // out of the budget together with the legend's measured height.
  legendGapPx?: number
  // Floor for the cap (e.g. one dot row), even on very short viewports.
  minHeightPx?: number
  // Cap to use before the first measurement (also the SSR value). Defaults
  // to maxHeight — pass a finite value when maxHeight is Infinity.
  initialHeightPx?: number
}) => {
  const { epilogue } = useHudContext()
  const hudContainerRef = epilogue.hudContainerRef
  const [cappedMaxHeight, setCappedMaxHeight] = useState(
    Math.min(maxHeight, initialHeightPx ?? maxHeight),
  )

  const measure = useCallback(() => {
    // The flex wrapper is the Tooltip's direct child; Tooltip clones
    // its own ref onto it, so we reach the wrapper via the legend
    // instead of putting a ref on it directly.
    const wrapper = legendRef.current?.parentElement
    if (!wrapper) return
    const wrapperTop = wrapper.getBoundingClientRect().top
    const hudContainer = hudContainerRef.current
    const hudRect = hudContainer?.getBoundingClientRect()
    const heightBelowWrapperTop = hudRect
      ? hudRect.height - (wrapperTop - hudRect.top)
      : window.innerHeight - wrapperTop
    const legendHeight = legendRef.current?.getBoundingClientRect().height ?? 0
    const legendReserve = legendPosition === "top" || legendPosition === "bottom"
      ? legendHeight + legendGapPx
      : 0
    const budget = Math.min(heightBelowWrapperTop, window.innerHeight) - legendReserve
    const available = Math.floor(budget / CAP_QUANTIZE_PX) * CAP_QUANTIZE_PX
    setCappedMaxHeight(Math.min(maxHeight, Math.max(minHeightPx, available)))
  }, [maxHeight, legendPosition, legendRef, legendGapPx, minHeightPx, hudContainerRef])

  // Coalesces measurement to one rAF: rect reads mid-commit force a
  // reflow, and the chart re-renders every frame while the scroll-
  // driven year animates. Next frame, layout is clean so reads are
  // cheap, and N renders per frame collapse into one measure.
  const measureRafIdRef = useRef<number | null>(null)
  const scheduleMeasure = useCallback(() => {
    if (measureRafIdRef.current !== null) return
    measureRafIdRef.current = requestAnimationFrame(() => {
      measureRafIdRef.current = null
      measure()
    })
  }, [measure])
  useEffect(() => {
    return () => {
      if (measureRafIdRef.current !== null) cancelAnimationFrame(measureRafIdRef.current)
    }
  }, [])

  // First measure is synchronous and pre-paint so the initial frame
  // never flashes an uncapped grid; afterwards (no dep array on purpose
  // — the wrapper's HUD offset shifts as the year-driven content above
  // it grows, re-rendering on the same animated date) measures are
  // rAF-coalesced. setState bails out when the cap is unchanged, no loop.
  const hasMeasuredOnceRef = useRef(false)
  useLayoutEffect(() => {
    if (!hasMeasuredOnceRef.current) {
      hasMeasuredOnceRef.current = true
      measure()
      return
    }
    scheduleMeasure()
  })

  useLayoutEffect(() => {
    window.addEventListener("resize", scheduleMeasure)
    // The legend can rewrap (e.g. on font load or label changes); its
    // height feeds the reserve, so observe it directly.
    const legend = legendRef.current
    const legendObserver = legend ? new ResizeObserver(scheduleMeasure) : null
    if (legend) legendObserver?.observe(legend)
    return () => {
      window.removeEventListener("resize", scheduleMeasure)
      legendObserver?.disconnect()
    }
  }, [scheduleMeasure, legendRef])

  return cappedMaxHeight
}
